/** Preferências locais do aparelho: tema e escopo (Meu / Família).
 * Ficam em localStorage porque são conveniência de quem está olhando, não dado do domínio.
 * Toda leitura e escrita é protegida: aba privada e site data bloqueado devolvem erro.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type Theme = "light" | "dark" | "system";

function read(key: string, fallback: string): string {
  try {
    return localStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}
function write(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* aba privada: a preferência vale só para esta visita */
  }
}

interface PrefsValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
  scope: string;
  setScope: (s: string) => void;
}

const Ctx = createContext<PrefsValue | null>(null);

export function PrefsProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => read("fluxo-theme", "system") as Theme);
  const [scope, setScopeState] = useState<string>(() => read("fluxo-scope", "mine"));

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "system") root.removeAttribute("data-theme");
    else root.setAttribute("data-theme", theme);
  }, [theme]);

  const setTheme = (t: Theme) => {
    setThemeState(t);
    write("fluxo-theme", t);
  };
  const setScope = (s: string) => {
    setScopeState(s);
    write("fluxo-scope", s);
  };

  return <Ctx.Provider value={{ theme, setTheme, scope, setScope }}>{children}</Ctx.Provider>;
}

export function usePrefs() {
  const v = useContext(Ctx);
  if (!v) throw new Error("usePrefs fora do PrefsProvider");
  return v;
}

export function isDark(theme: Theme) {
  if (theme === "dark") return true;
  if (theme === "light") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}
